import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import styles from "./EmployeeDetail.module.css";
import { testUrl } from "../home/BaseData";
// Adjust path to BaseData if needed. If it's in src/user/home/BaseData.js and we are in src/user/employees/
// then ../home/BaseData is correct.

const EmployeeDetail = () => {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(null);

    const tabOptions = [
        { key: "kitoblar", label: "KITOBLAR" },
        { key: "maqolalar", label: "MAQOLALAR" },
        { key: "avtoreferat", label: "AVTOREFERAT (PHD, DSC)" },
        { key: "loyihalar", label: "LOYIHALAR" },
        { key: "sertifikatlar", label: "SERTIFIKATLAR" },
        // Video excluded
    ];

    useEffect(() => {
        const fetchEmployee = async () => {
            try {
                const response = await axios.get(`${testUrl}/api/employees/${id}/`);
                setEmployee(response.data);
            } catch (error) {
                console.error("Error fetching employee details:", error);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchEmployee();
        }
    }, [id, i18n.language]);

    if (loading) return <div className={styles.container}>{t("loading")}</div>;
    if (!employee) return <div className={styles.container}>Xodim topilmadi</div>;

    const currentLang = i18n.language; // 'uz', 'ru', 'en'
    const name = employee[`name_${currentLang}`] || employee.name_uz;
    const position = employee[`position_${currentLang}`] || employee.position_uz; // This might come from RectorEmployee?? 
    // Wait, Employee model doesn't have position! RectorEmployee has position. 
    // But standard Employee has 'specialization'.
    // However, the card logic used `position`. 
    // Let's check Employee model again.
    // Employee model: specialization_uz (RichText).
    // RectorEmployee model: position_uz (Char).
    // The API `EmployeesSerializer` includes `specialization`. 
    // If this employee is NOT a Rector, they might not have a position field in the main object unless added specially?
    // Use `specialization` as fallback or check if API provides `position` (maybe via join?). 
    // Standard employees might just have specialization.

    // Actually, checking the card logic again:
    // Card used: `const position = employee[\`position_${language}\`] || employee.position_uz;`
    // But standard Employee model DOES NOT have `position`.
    // `RectorEmployee` has it.
    // Maybe the `employee` object passed to card WAS a `RectorEmployee` or a "flattened" object from the Faculty API?
    // Faculty API returns `employees` list. These might be `DepartmentEmployee` or `KafedraEmployee` which have `position`.
    // Bringing strict `Employee` from `/api/employees/:id` might miss `position` if `position` is stored in the linking table (DepartmentEmployee).

    // If the user needs Position, and it's in a linking table, fetching `/api/employees/:id` (which is the core Employee model) won't show it.
    // I might need to fetch `DepartmentEmployee` or rely on `specialization`.
    // For now, I'll use `specialization` or try to find `position` if available.

    const about = employee[`about_${currentLang}`] || employee.about_uz;
    const email = employee.email;

    // Parse details
    const details = employee.details || [];
    const filteredFiles = details.filter(file => file.type === activeTab);

    return (
        <div className={styles.container}>
            <div className={styles.profileCard}>
                <div className={styles.imageWrapper}>
                    <img
                        src={employee.image || "/media/employees/avatar.png"}
                        alt={name}
                        className={styles.image}
                    />
                </div>
                <div className={styles.profileInfo}>
                    <h1 className={styles.name}>{name}</h1>

                    {/* If position exists (from API if extended) */}
                    {employee[`position_${currentLang}`] && (
                        <div className={styles.detailRow}>
                            <span className={styles.label}>{t("Lavozimi")}:</span>
                            <span className={styles.value}>{employee[`position_${currentLang}`]}</span>
                        </div>
                    )}

                    {/* Note: Standard Employee model fields follow */}
                    <div className={styles.detailRow}>
                        <span className={styles.label}>{t("email")}:</span>
                        <a href={`mailto:${email}`} className={styles.email}>{email}</a>
                    </div>

                    {employee[`work_experience_${currentLang}`] && (
                        <div className={styles.detailRow}>
                            <span className={styles.label}>{t("ish_staj") || "Ish tajribasi"}:</span>
                            <span className={styles.value}>{employee[`work_experience_${currentLang}`]}</span>
                        </div>
                    )}

                </div>
            </div>

            <div className={styles.aboutSection}>
                <div dangerouslySetInnerHTML={{ __html: about }} />
            </div>

            <div className={styles.tabsContainer}>
                {tabOptions.map((tab) => (
                    <button
                        key={tab.key}
                        className={`${styles.tabButton} ${activeTab === tab.key ? styles.active : ''}`}
                        onClick={() => setActiveTab(activeTab === tab.key ? null : tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab && (
                <div className={styles.contentSection}>
                    {filteredFiles.length > 0 ? (
                        <div className={styles.fileList}>
                            {filteredFiles.map((file) => (
                                <div key={file.id} className={styles.fileItem}>
                                    <span className={styles.fileTitle}>{file.title}</span>
                                    <a
                                        href={file.file}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.downloadButton}
                                        download
                                    >
                                        {t("download") || "Yuklab olish"}
                                    </a>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p style={{ textAlign: "center", color: "#666" }}>
                            {t("noFilesAvailable") || "Hozircha hech qanday fayl mavjud emas"}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default EmployeeDetail;
